import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const ChurnRate = () => {
  // State variables for Churn Rate calculation
  const [lostCustomers, setLostCustomers] = useState(0); // Number of lost customers
  const [totalCustomersStart, setTotalCustomersStart] = useState(0); // Total customers at the start of the period
  const [churnRate, setChurnRate] = useState(0); // Calculated churn rate

  // Event handlers to update state values
  const handleLostCustomersChange = (e) => setLostCustomers(parseFloat(e.target.value) || 0);
  const handleTotalCustomersStartChange = (e) => setTotalCustomersStart(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate churn rate when any input changes
  useEffect(() => {
    if (totalCustomersStart && lostCustomers) {
      const rate = (lostCustomers / totalCustomersStart) * 100; // Calculate churn rate
      setChurnRate(rate);
    } else {
      setChurnRate(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [lostCustomers, totalCustomersStart]);

  return (
    <Layout title="Churn Rate Calculator" path="churn-rate">
      <section className="section-padding" id="churn-rate-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Churn Rate Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Churn Rate Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Lost Customers Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="lostCustomers" className="form-label">
                        Lost Customers
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Lost Customers:</strong> Enter the number of customers who left during the given period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="lostCustomers"
                        placeholder="Enter number of lost customers"
                        value={lostCustomers || ""}
                        onChange={handleLostCustomersChange}
                      />
                    </div>

                    {/* Total Customers at Start Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalCustomersStart" className="form-label">
                        Total Customers at Start
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Customers at Start:</strong> Enter the total number of customers at the beginning of the period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalCustomersStart"
                        placeholder="Enter total number of customers at start"
                        value={totalCustomersStart || ""}
                        onChange={handleTotalCustomersStartChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Churn Rate:{" "}
                        <span className="fw-bold ms-3">
                          {churnRate === 0 ? "0.00" : churnRate.toFixed(2)} %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Churn Rate Calculator
                </h3>

                {/* What is a Churn Rate Calculator? */}
                <h4 className="mt-3 mb-3">What is a Churn Rate Calculator?</h4>
                <p>
                  A **Churn Rate Calculator** helps you measure the percentage of customers lost during a specific period. By comparing the number of lost customers to the total number of customers at the start of the period, it provides a clear indicator of customer retention and business performance.
                </p>

                {/* Why Use a Churn Rate Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Churn Rate Calculator?</h4>
                <p>
                  This calculator is crucial for businesses to track customer retention and identify potential issues with customer satisfaction or product offerings. By understanding your churn rate, you can take steps to improve customer engagement and reduce losses.
                </p>

                {/* Benefits of Using a Churn Rate Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Churn Rate Calculator</h4>
                <ul>
                  <li><strong>Helps businesses identify retention issues:</strong> By tracking churn, you can spot trends that may indicate problems with customer loyalty or satisfaction.</li>
                  <li><strong>Guides customer retention strategies:</strong> Knowing your churn rate allows you to adjust marketing, customer support, and engagement strategies to retain more customers.</li>
                  <li><strong>Monitors business health:</strong> A high churn rate may indicate underlying issues in your business that need to be addressed, such as poor customer service or product quality.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Churn Rate Calculator?</h4>
                <p>
                  The Churn Rate Calculator is ideal for:
                </p>
                <ul>
                  <li><strong>Businesses with subscription models:</strong> SaaS companies, gyms, and media platforms rely heavily on customer retention.</li>
                  <li><strong>Customer relationship managers:</strong> Provides insights for improving engagement and reducing churn.</li>
                  <li><strong>Marketing and sales teams:</strong> Helps refine strategies to reduce churn and improve customer loyalty.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is a good churn rate?</h4>
                <p>
                  A "good" churn rate depends on your industry. For example, SaaS companies typically aim for a churn rate under 5%, while other industries may have different benchmarks. It’s important to compare your churn rate to industry standards and track it over time to see if improvements are needed.
                </p>

                <h4 className="mt-4 mb-3">How can I reduce my churn rate?</h4>
                <p>
                  To reduce churn, businesses can focus on improving customer satisfaction, offering excellent customer service, creating loyalty programs, and continuously improving products or services to meet customer needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ChurnRate;
